/* eslint-disable confluence-feature-gating/no-preconditioning */
'use no memo';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { css, styled, keyframes } from '@compiled/react';
import memoize from 'memoize-one';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Text from '@atlaskit/primitives/text';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';

import { expValEquals } from '@confluence/feature-experiments';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { MutationUpdaterInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { CommentRenderer } from '@confluence/comment/entry-points/CommentRenderer.ts';
import type {
	CommentInlineResolveProperties,
	CommentReactionsSummary as ReactionsSummary,
	FooterResolvedProperties,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import type { SitePermissionType } from '@confluence/comments-data-manager/entry-points/useAllCommentsDataQueryTypes.ts';
import { fg } from '@confluence/feature-gating';
import { CollapsibleCommentContent } from '@confluence/agent-mention/entry-points/CollapsibleCommentContent.ts';
import {
	CommentType,
	OPTIMISTIC_SAVE_COMMENT_ID,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useContentType } from '@confluence/page-context';
import { useIsNewDanglingCommentExperienceEnabled } from '@confluence/comments-panel-utils/entry-points/useIsNewDanglingCommentExperienceEnabled.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo.ts';
import { getUnreadCommentsIntersectionObserver } from '@confluence/unread-comments/entry-points/UnreadCommentsIntersectionObserver.ts';
import { useViewedComments } from '@confluence/unread-comments/entry-points/useViewedComments.ts';
import {
	isValidUnreadComment,
	buildValidationObjectFromProps,
} from '@confluence/unread-comments/entry-points/unreadCommentsUtils.ts';

import type {
	CommentPermissions,
	CommentContributor,
	InlineCommentsMode,
	CommentAction,
} from './inlineCommentsTypes.ts';
import { DanglingContent } from './DanglingContent.tsx';
import { CommentAuthor } from './CommentAuthor.tsx';
import { WithCurvedBranchingStyle } from './WithCurvedBranchingStyle.tsx';
import { useCommentActions } from './hooks/useCommentActions.tsx';
import { useResizeObserver } from './hooks/useResizeObserver.ts';
import { useCommentHeightAnalytics } from './hooks/useCommentHeightAnalytics.ts';
import { getCommentRenderLocation } from './commentHeightAnalyticsUtils.ts';

type CommentContainerProps = {
	isFocused?: boolean;
	isRemoving?: boolean;
	mode: InlineCommentsMode;
	isCommentsPanel?: boolean;
	isFabricPage?: boolean;
};

const fadeOut = keyframes({
	from: { opacity: 1 },
	to: { opacity: 0.5 },
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentContainer = styled.div<CommentContainerProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	`
  display: flex;
  flex-direction: column;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
	padding: ${(props: CommentContainerProps) => {
		const horizontalSpacing = token('space.150');

		const padding = props.isFabricPage
			? `${token('space.150')} ${horizontalSpacing} ${token('space.0')} ${horizontalSpacing}`
			: `${token('space.150')} ${token('space.200')}`;

		const contextDependentPadding = props.isCommentsPanel === true ? 'inherit' : padding;

		return props.mode === 'view-all' ? token('space.400') : contextDependentPadding;
	}};
  border-left: 0;
  border-top: 0;
  animation-duration: 0.5s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  ${(props: CommentContainerProps) => Boolean(props.isRemoving) && `animation: ${fadeOut};`}
  animation-timing-function: linear;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  cursor: ${(props: CommentContainerProps) => (props.isRemoving ? 'not-allowed' : 'inherit')};
  outline: none;

${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  background: ${(props: CommentContainerProps) =>
		props.isFocused && !props.isCommentsPanel
			? `${token('color.background.selected')}`
			: undefined};

  blockquote {
    color: ${token('color.text.subtlest')};
  }

  &:last-of-type {
    border-bottom: 0;
  }
`,
);

const standaloneStyle = css({
	marginLeft: token('space.150'),
});

const standaloneCommentWithoutRepliesStyle = css({
	paddingLeft: token('space.300'),
});

const withLeftBorderStyle = css({
	borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	paddingBottom: token('space.100'),
});

const withLeftBorderStyleUpdated = css({
	borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	paddingBottom: token('space.100'),
});

const standaloneLeftBorderStyle = css({
	borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	paddingLeft: token('space.250'),
	paddingBottom: token('space.0'),
});

const withoutLeftBorderStyle = css({
	paddingBottom: token('space.100'),
});

const commentBodyStyles = css({
	marginTop: token('space.100'),
	marginLeft: token('space.150'),
	paddingLeft: token('space.300'),
	paddingRight: token('space.050'),
});

/** Whether a non-reply comment draws the thread rail down its author avatar's centre. */
export function drawsThreadRail({
	hideBranchingStyle,
	numReplies,
	canAddComments,
}: {
	hideBranchingStyle?: boolean;
	numReplies: number;
	canAddComments?: boolean;
}): boolean {
	return !hideBranchingStyle && (numReplies !== 0 || Boolean(canAddComments));
}

const commentReplyStyles = css({
	marginLeft: token('space.0'),
	paddingLeft: token('space.0'),
});

const commentBodyReplyStyles = css({
	marginTop: token('space.100'),
});

const commentBodyNestedReplyStyle = css({
	marginLeft: token('space.100'),
});

const replyCommentBodyStyle = css({
	paddingLeft: token('space.100'),
});

const annotatedTextBodyStyle = xcss({
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	padding: 'space.200',
	width: '100%',
	backgroundColor: 'elevation.surface',
	marginBottom: 'space.150',
});

const danglingAnnotatedTextBodyStyle = xcss({
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
	width: '100%',
	backgroundColor: 'elevation.surface',
	marginBottom: 'space.150',
});

// Lets `DanglingContent`'s tinted header reach these edges: padding moves onto its rows, and
// overflow clips the header to the rounded corners.
const danglingAnnotatedTextBodyEdgeToEdgeStyle = xcss({
	paddingTop: 'space.0',
	paddingBottom: 'space.0',
	paddingLeft: 'space.0',
	paddingRight: 'space.0',
	overflow: 'hidden',
});

const annotatedTextBodyWithPressedStyle = xcss({
	background: token('elevation.surface.pressed'),
});

const dotBaseStyles = xcss({
	height: token('space.100'),
	width: token('space.100'),
	backgroundColor: 'color.background.brand.bold',
	borderRadius: 'radius.full',
	margin: 'space.050',
	transition: 'opacity 300ms ease-in-out',
});

const dotVisibleStyles = xcss({
	opacity: 1,
});

const dotHiddenStyles = xcss({
	opacity: 0,
});

const replyBranchStyles = css({
	position: 'relative',
	// for the curvy border
	'&::before': {
		content: "''",
		position: 'absolute',
		left: '-16px',
		top: '16px',
		height: `var(--replyBranchHeight)`,
		bottom: '30px',
		borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		paddingLeft: token('space.200'),
	},
});

const replyBranchStylesUpdated = css({
	position: 'relative',
	// for the curvy border
	'&::before': {
		content: "''",
		position: 'absolute',
		left: '-16px',
		top: '16px',
		height: `var(--replyBranchHeight)`,
		bottom: '30px',
		borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		paddingLeft: token('space.200'),
	},
});

const footerCommentAuthorStyles = xcss({
	paddingTop: 'space.100',
});

const replyCommentAuthorStyles = xcss({
	paddingLeft: 'space.050',
});

type CommentBodyProps = {
	pageId: string;
	pageType: string;
	avatarUrl?: string;
	agentChatMenuItem?: React.ReactNode;
	commentId: string;
	content: string;
	date: string;
	displayName?: string | null;
	permissions: CommentPermissions;
	dateUrl?: string;
	createdAt?: string;
	createdAtNonLocalized?: string;
	versionByDisplayName?: string | null;
	versionDate: string;
	userId?: string | null;
	deleteComment?: () => void;
	editComment?: () => void;
	resolveComment?: () => void;
	isReply: boolean;
	isEdited: boolean;
	parentCommentId?: string | null;
	isFocused?: boolean;
	shouldScrollIntoView?: boolean;
	isRemoving?: boolean;
	numReplies?: number;
	isFabricPage?: boolean;
	isCommentActive?: boolean;
	isCurrentUserAnonymous?: boolean;
	mode: InlineCommentsMode;
	onRendered?: () => void;
	shouldAutofocus?: boolean;
	onAutoFocused?: () => void;
	permissionType?: SitePermissionType;
	maxHeight?: number;
	fadeOutHeight?: number;
	onComplete?: () => void;
	supportedActions?: CommentAction[];
	isUnread?: boolean;
	isCommentsPanel?: boolean;
	isMediaViewerComment?: boolean;
	resolveProperties?: CommentInlineResolveProperties | FooterResolvedProperties;
	annotationId?: string;
	annotatedText?: string | null;
	reopenComment?: () => void;
	replyToComment?: (commentId: string) => void;
	inheritedReactionsData?: ReactionsSummary | null;
	reactionsReadOnly?: boolean;
	reactionsCacheUpdateFn?: (
		emojiId: string,
		actionType: 'add' | 'delete',
		contentId: string,
		containerId?: string,
	) => MutationUpdaterInterop<any> | void;
	currentlySelectedThreadKey?: string;
	setCurrentlySelectedThreadKey?: (markerRef: string) => void;
	isAnnotatedTextPressed?: boolean;
	setAnnotatedTextPressed?: (state: boolean) => void;
	commentType: CommentType;
	adjustForNestedReplies?: boolean;
	/**
	 * Extra pixels added to the reply's connector-line height (`--replyBranchHeight`). Used when
	 * something is rendered below the comment body but outside its measured container (e.g. the
	 * agent-session status row), so the line spans that extra height and reaches the next reply.
	 */
	extraReplyBranchHeight?: number;
	hideBranchingStyle?: boolean;
	canAddComments?: boolean;
	isParentCommentOpen?: boolean;
	isFooterComment?: boolean;
	adjustForFooterNestedReplies?: boolean;
	isStandaloneComment?: boolean;
	isOffline?: boolean;
	showRepliesWithCurvedBorders?: boolean;
	replyHasNestedObjects?: boolean;
	contributors?: ReadonlyArray<CommentContributor> | null;
};

// Parse content to JSON or Fail view inline experience and return null
const parseContent = memoize((content, experienceTracker) => {
	if (!content?.length) return null;

	try {
		return JSON.parse(content);
	} catch (error) {
		experienceTracker.stopOnError({
			name: VIEW_INLINE_COMMENT_EXPERIENCE,
			error,
			attributes: {
				stopPoint: 'inline-comments-common/CommentBody',
			},
		});
		return null;
	}
});

const STABLE_SUPPORTED_ACTIONS_DEFAULT: CommentAction[] = ['edit', 'delete', 'resolve'];

export const CommentBody = ({
	pageId,
	pageType,
	avatarUrl,
	agentChatMenuItem,
	commentId,
	content,
	date,
	createdAt,
	createdAtNonLocalized,
	versionByDisplayName,
	versionDate,
	displayName,
	permissions,
	dateUrl,
	userId,
	deleteComment,
	editComment,
	resolveComment,
	isReply,
	isEdited,
	parentCommentId,
	isFocused,
	shouldScrollIntoView: _shouldScrollIntoView,
	isRemoving,
	numReplies,
	isFabricPage,
	isCommentActive,
	isCurrentUserAnonymous,
	mode,
	onRendered,
	shouldAutofocus,
	onAutoFocused,
	permissionType,
	maxHeight,
	fadeOutHeight,
	onComplete,
	supportedActions = STABLE_SUPPORTED_ACTIONS_DEFAULT,
	isUnread = false,
	isCommentsPanel = false,
	isMediaViewerComment = false,
	resolveProperties,
	annotationId,
	annotatedText,
	reopenComment,
	replyToComment,
	inheritedReactionsData,
	reactionsReadOnly,
	reactionsCacheUpdateFn,
	currentlySelectedThreadKey,
	setCurrentlySelectedThreadKey,
	isAnnotatedTextPressed,
	setAnnotatedTextPressed,
	commentType = CommentType.INLINE,
	adjustForNestedReplies = false,
	extraReplyBranchHeight = 0,
	hideBranchingStyle = false,
	canAddComments = true,
	isParentCommentOpen,
	isFooterComment = false,
	isStandaloneComment = false,
	isOffline = false,
	showRepliesWithCurvedBorders,
	replyHasNestedObjects = false,
	contributors,
}: CommentBodyProps): React.JSX.Element => {
	const [hasFallbackContent, setHasFallbackContent] = useState(false);
	const [isHoveredLocal, setIsHoveredLocal] = useState(false);
	const isMouseOverContainerRef = useRef(false);

	const isSelected = currentlySelectedThreadKey === annotationId;
	const [contentType] = useContentType();
	const isNewDanglingCommentExperienceEnabled = useIsNewDanglingCommentExperienceEnabled();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const [containerRef, replyBranchHeight] = useResizeObserver();

	// Track the currently observed element for cleanup
	const [observedElement, setObservedElement] = useState<HTMLElement | null>(null);

	const { getViewedCommentsCacheOnly } = useViewedComments({ pageId: pageId || '' });
	const { userId: currentUserId } = useSessionData();

	// Create a ref callback to set the ref and observe when the DOM element is available
	const handleUnreadCommentRef = useCallback(
		(node: HTMLDivElement | null) => {
			// isUnread comes from unreadCommentIDSet and doesn't contain dangling and resolved, we're only using that for the unread indicators
			// for observing unread comments, we'll rely on the viewedComments set as the source of truth
			const viewedCommentsResult = getViewedCommentsCacheOnly();
			const viewedComments =
				viewedCommentsResult.success && viewedCommentsResult.data
					? viewedCommentsResult.data
					: new Set<string>();

			const commentForValidation = buildValidationObjectFromProps({
				commentId,
				userId,
				createdAt,
				commentType: commentType === CommentType.INLINE ? 'INLINE' : 'FOOTER',
				resolveProperties: { resolved: false }, // overriding resolveProperties since we still want to observe resolved comments for unread
			});

			const isValidUnread = isValidUnreadComment({
				comment: commentForValidation,
				currentUserId: currentUserId || '',
				viewedComments,
			});

			const shouldObserve = node && isValidUnread;
			if (shouldObserve) {
				const observer = getUnreadCommentsIntersectionObserver();
				observer.observeComment(node);
				setObservedElement(node);
			} else {
				// If we shouldn't observe this node, clear the observed element
				setObservedElement(null);
			}
		},
		[commentId, userId, createdAt, commentType, getViewedCommentsCacheOnly, currentUserId],
	);

	const { pageInfo } = usePageInfo();
	const isArchived = pageInfo?.status === 'archived';

	const { mainCommentActions, hoveredActionItems, isDropdownOpen } = useCommentActions({
		agentChatMenuItem,
		pageId,
		pageType,
		commentId,
		permissions,
		isReply,
		parentCommentId,
		deleteComment,
		editComment,
		resolveComment,
		restrictEdit: hasFallbackContent,
		restrictDelete: Boolean(numReplies),
		numReplies,
		mode,
		commentDateUrl: dateUrl,
		supportedActions,
		isCommentsPanel,
		isMediaViewerComment,
		isHovered: isHoveredLocal,
		annotationId,
		resolveProperties: resolveProperties as
			| CommentInlineResolveProperties
			| FooterResolvedProperties,
		reopenComment,
		replyToComment,
		inheritedReactionsData,
		reactionsReadOnly,
		reactionsCacheUpdateFn,
		commentType,
		hideBranchingStyle,
		canAddComments,
		isSelected,
		isParentCommentOpen,
		isFooterComment,
		isStandaloneComment,
		isOffline,
		isFabricPage,
		isArchived,
		authorName: displayName,
	});

	const isHoveredComment = isHoveredLocal || isDropdownOpen;

	const adf = parseContent(content, experienceTracker);

	const commentHeightRef = useCommentHeightAnalytics({
		isEnabled:
			Boolean(adf) &&
			commentId !== OPTIMISTIC_SAVE_COMMENT_ID &&
			expValEquals('confluence_comment_height_analytics', 'isEnabled', true),
		pageId,
		contentType: pageType,
		commentId,
		commentType,
		renderLocation: getCommentRenderLocation({
			isFooterComment,
			isStandaloneComment,
			isCommentsPanel,
		}),
		isReply,
		mode,
		adf,
	});

	useEffect(() => {
		if (shouldAutofocus) {
			containerRef.current &&
				containerRef.current.focus({
					preventScroll: true,
				});
			onAutoFocused && onAutoFocused();
		}
	}, [containerRef, shouldAutofocus, onAutoFocused]);

	// clean up the observer when the component unmounts
	useEffect(() => {
		return () => {
			if (observedElement) {
				const observer = getUnreadCommentsIntersectionObserver();
				observer.unobserveComment(observedElement);
			}
		};
	}, [observedElement]);

	const onCommentContentFallback = useCallback(() => setHasFallbackContent(true), []);
	const renderedComment = adf ? (
		<CommentRenderer
			commentId={commentId}
			adf={adf}
			onContentFallback={onCommentContentFallback}
			isTruncatedContent={mode === 'view-all' && !isCommentActive}
			onRendered={onRendered}
			onComplete={onComplete}
			maxHeight={maxHeight}
			fadeOutHeight={fadeOutHeight}
			isInlineComment
			isCommentsPanel={isCommentsPanel}
			isEdited={isEdited}
			versionByDisplayName={versionByDisplayName}
			versionDate={versionDate}
			commentType={commentType}
		/>
	) : null;

	const handleMouseEventOnWrapper = (eventType: 'enter' | 'leave') => () => {
		isMouseOverContainerRef.current = eventType === 'enter';
		if (commentId !== OPTIMISTIC_SAVE_COMMENT_ID) {
			setIsHoveredLocal(eventType === 'enter');
		}
	};

	const shouldShowOverflowMenu = supportedActions.length > 0;

	const showDefaultReplyStyle = showRepliesWithCurvedBorders ?? (isCommentsPanel && isReply);

	const getHighlightBackgroundColor = () => {
		if (isAnnotatedTextPressed) {
			return token('color.background.accent.yellow.subtle.hovered');
		}
		if (isSelected) {
			return token('color.background.accent.yellow.subtle');
		}
		return 'inherit';
	};

	const handleMouseUp = () => {
		if (annotationId && setCurrentlySelectedThreadKey) {
			setAnnotatedTextPressed && setAnnotatedTextPressed(false);
		}
	};

	const showContentUnavailableForDanglingComment = (
		resolveProperties as CommentInlineResolveProperties
	)?.isDangling;

	// Slides flag every thread as dangling until their iframe has sent its comment markers, so only
	// trust the flag once the dangling experience reports itself enabled.
	const isDetachedSlideThread =
		contentType === 'slide' &&
		!!showContentUnavailableForDanglingComment &&
		isNewDanglingCommentExperienceEnabled;

	// Databases and slides never quote the highlighted text. `cc_comments_detached_in_open_view`
	// removes the "Deleted content" view, leaving the deleted-content header as the only sign a
	// thread is detached, so `cc_comments_detached_slides_label` shows it on detached slide threads.
	const shouldShowCommentContentContext =
		isCommentsPanel &&
		!isReply &&
		((!!annotatedText && contentType !== 'database' && contentType !== 'slide') ||
			(isDetachedSlideThread &&
				expValEquals('cc_comments_detached_in_open_view', 'isEnabled', true) &&
				expValEquals('cc_comments_detached_slides_label', 'isEnabled', true)));

	return (
		<CommentContainer
			ref={containerRef}
			data-comment-id={commentId}
			data-testid={commentId}
			isFocused={isFocused}
			isRemoving={isRemoving}
			mode={mode}
			isCommentsPanel={isCommentsPanel}
			role="article"
			tabIndex={-1}
			onMouseEnter={handleMouseEventOnWrapper('enter')}
			onMouseLeave={handleMouseEventOnWrapper('leave')}
			onFocusCapture={() => {
				if (commentId !== OPTIMISTIC_SAVE_COMMENT_ID) {
					setIsHoveredLocal(true);
				}
			}}
			onBlurCapture={(e: React.FocusEvent) => {
				const next = e.relatedTarget as Node | null;
				const container = e.currentTarget;
				if (next && container.contains(next)) return;
				// Only clear hover when focus leaves if the mouse has left too – mouse takes precedence
				if (!isMouseOverContainerRef.current) {
					setIsHoveredLocal(false);
				}
			}}
			css={{ position: 'relative ' }}
			isFabricPage={isFabricPage}
		>
			<div ref={handleUnreadCommentRef} data-comment-id={commentId}>
				<div
					css={[
						isReply && isCommentsPanel && !hideBranchingStyle && replyBranchStyles,
						// updated border
						isReply &&
							isCommentsPanel &&
							!hideBranchingStyle &&
							// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
							fg('confluence_comment_border_migration') &&
							replyBranchStylesUpdated,
					]} // We need to adjust the size of the border if we have a nested reply scroller below this
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={
						!hideBranchingStyle
							? ({
									'--replyBranchHeight': `${replyBranchHeight + (adjustForNestedReplies ? 53 : 0) + extraReplyBranchHeight}px`,
								} as React.CSSProperties)
							: {}
					}
				>
					<WithCurvedBranchingStyle
						showDefaultStyles={showDefaultReplyStyle}
						customStyles={{ left: '-16px', height: '24px' }}
					>
						<Box
							xcss={[
								isFooterComment || (isCommentsPanel && isReply)
									? footerCommentAuthorStyles
									: undefined,
								isCommentsPanel && isReply && replyCommentAuthorStyles,
							]}
						>
							<Inline testId="comment-author-box" spread="space-between" alignBlock="center">
								<CommentAuthor
									commentMode="view"
									userId={userId}
									date={date}
									createdAtNonLocalized={createdAtNonLocalized}
									versionByDisplayName={versionByDisplayName}
									versionDate={versionDate}
									displayName={displayName}
									avatarUrl={avatarUrl}
									commentId={isCurrentUserAnonymous ? commentId : null}
									permissionType={permissionType}
									isInactiveComment={Boolean(mode === 'view-all' && !isCommentActive)}
									contributors={contributors}
									resolveProperties={
										resolveProperties as CommentInlineResolveProperties | FooterResolvedProperties
									}
									size="small"
									isCommentsPanel={isCommentsPanel}
									isGeneralComments={commentType === CommentType.GENERAL}
									isReply={isReply}
									isEdited={isEdited}
									isFooterComment={isFooterComment}
									isStandaloneComment={isStandaloneComment}
								/>
								<div
									data-testid={`unread-comment-indicator-${commentId}`}
									// Conditionally rendering this element can cause layout shift when the comment is hovered
									// and the layout is recalculated, so we're using css to hide it instead
									style={{ visibility: isHoveredComment ? 'hidden' : 'visible' }}
									aria-hidden={isHoveredComment || !isUnread}
								>
									<Box xcss={[dotBaseStyles, isUnread ? dotVisibleStyles : dotHiddenStyles]} />
								</div>
							</Inline>
						</Box>
					</WithCurvedBranchingStyle>
				</div>
				{shouldShowOverflowMenu && hoveredActionItems}
				<div
					css={[
						isCommentsPanel && commentBodyStyles,
						isCommentsPanel && isReply && commentReplyStyles,
						isCommentsPanel && withoutLeftBorderStyle,
						isCommentsPanel &&
							!isReply &&
							!hideBranchingStyle &&
							(numReplies !== 0 || canAddComments) &&
							withLeftBorderStyle,
						isCommentsPanel &&
							!isReply &&
							!hideBranchingStyle &&
							(numReplies !== 0 || canAddComments) &&
							// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
							fg('confluence_comment_border_migration') &&
							withLeftBorderStyleUpdated,
						isStandaloneComment &&
							!isReply &&
							!hideBranchingStyle &&
							(numReplies !== 0 || canAddComments) &&
							standaloneLeftBorderStyle,
						isFooterComment && isReply && commentBodyReplyStyles,
						isFooterComment && isReply && replyHasNestedObjects && commentBodyNestedReplyStyle,
						isStandaloneComment && isReply && commentBodyReplyStyles,
						isCommentsPanel && isReply && commentBodyReplyStyles,
						!isCommentsPanel && standaloneStyle,
						!isCommentsPanel && numReplies === 0 && standaloneCommentWithoutRepliesStyle,
					]}
				>
					{shouldShowCommentContentContext && (
						<Inline space="space.150" alignBlock="center" testId="highlight-text">
							<Box
								data-testId="annotated-section-for-inlineComment"
								xcss={[
									showContentUnavailableForDanglingComment
										? danglingAnnotatedTextBodyStyle
										: annotatedTextBodyStyle,
									showContentUnavailableForDanglingComment &&
										expValEquals('cc_comments_detached_in_open_view', 'isEnabled', true) &&
										danglingAnnotatedTextBodyEdgeToEdgeStyle,
									isAnnotatedTextPressed &&
										!showContentUnavailableForDanglingComment &&
										annotatedTextBodyWithPressedStyle,
								]}
								onMouseDown={() => {
									if (annotationId) {
										setAnnotatedTextPressed && setAnnotatedTextPressed(true);
									}
								}}
								onMouseUp={handleMouseUp}
							>
								{showContentUnavailableForDanglingComment ? (
									<DanglingContent
										annotatedText={annotatedText}
										hideDeletedText={isDetachedSlideThread}
									/>
								) : (
									<Text size="small" maxLines={3}>
										<span
											style={{
												background: getHighlightBackgroundColor(),
												borderBottom:
													isAnnotatedTextPressed || isSelected
														? `2px solid ${token('color.icon.accent.yellow')}`
														: '',
											}}
										>
											{annotatedText}
										</span>
									</Text>
								)}
							</Box>
						</Inline>
					)}
					{renderedComment && (
						<div ref={commentHeightRef} css={[isCommentsPanel && isReply && replyCommentBodyStyle]}>
							{commentType === CommentType.INLINE &&
							!(mode === 'view-all' && !isCommentActive) &&
							// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
							fg('platform_editor_comments_show_more') &&
							expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ? (
								<CollapsibleCommentContent>{renderedComment}</CollapsibleCommentContent>
							) : (
								renderedComment
							)}
						</div>
					)}
				</div>
				{shouldShowOverflowMenu && mainCommentActions}
			</div>
		</CommentContainer>
	);
};
